import type { ReactNode } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { PlanRecurrence, useListPlans } from '@/api/v1/billing/plans'
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
} from '@/components/common/responsive-dialog'
import { useComponentTranslation } from '@/hooks/use-component-translation'
import { useNavigateWithSearchParams } from '@/hooks/use-navigate-with-search-params'
import {
  getAnnualSavingsPercent,
  getSkusForRecurrence,
  getVolumeBuckets,
  UNLIMITED_ENVELOPES,
} from '@/lib/plans'
import { cn } from '@/lib/utils'
import { usePlanFinderDialogStore } from '@/stores/plan-finder-dialog'

import locales from './locales'
import type {
  FeatureKey,
  RecommendedSku,
  UsageProfile,
} from './plan-finder.utils'
import { getRecommendedSku, getRecurrenceQuote } from './plan-finder.utils'
import {
  trackPlanFinderClosed,
  trackPlanFinderEnvelopesSelected,
  trackPlanFinderFeaturesSelected,
  trackPlanFinderPlanSelected,
  trackPlanFinderStarted,
  trackPlanFinderUsageSelected,
} from './plan-finder-dialog.tracking'
import { EnvelopesStep } from './steps/envelopes-step'
import { FeaturesStep } from './steps/features-step'
import { NoMatchStep } from './steps/no-match-step'
import { ResultStep } from './steps/result-step'
import { UsageStep } from './steps/usage-step'
import { WelcomeStep } from './steps/welcome-step'

interface PlanFinderDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

// Steps: 0 = welcome, 1 = usage profile, 2 = envelopes, 3 = features, 4 = result
const QUIZ_STEPS = ['usage', 'envelopes', 'features'] as const
const TOTAL_QUIZ_STEPS = QUIZ_STEPS.length

export function PlanFinderDialog({
  open,
  onOpenChange,
}: PlanFinderDialogProps) {
  const navigateWithSearchParams = useNavigateWithSearchParams()
  const { data: plansData } = useListPlans()

  const skus = useMemo(() => plansData?.items ?? [], [plansData])
  const source = usePlanFinderDialogStore((s) => s.source)
  const hasTrackedOpen = useRef(false)

  useEffect(() => {
    if (open && source && !hasTrackedOpen.current) {
      hasTrackedOpen.current = true
      trackPlanFinderStarted({ source })
    }
    if (!open) {
      hasTrackedOpen.current = false
    }
  }, [open, source])

  const { t } = useComponentTranslation(locales)
  const [currentStep, setCurrentStep] = useState(0)
  const [usageProfile, setUsageProfile] = useState<UsageProfile>(null)
  const [selectedRecurrence, setSelectedRecurrence] = useState<
    'Monthly' | 'Yearly'
  >('Yearly')
  const [envelopeOption, setEnvelopeOption] = useState<string | null>(null)
  const [customEnvelopes, setCustomEnvelopes] = useState('')
  const [selectedFeatures, setSelectedFeatures] = useState<Array<FeatureKey>>([
    'eSignatures',
  ])

  const isSeasonal = usageProfile === 'seasonal'

  // The finite buckets ARE the catalog's variants, so the wizard can only ever
  // recommend something that exists. "Ilimitado" is not offered as its own
  // option (owner instruction): "custom" (Outro) stands for above-the-ladder
  // demand — more than the biggest bucket, or "I don't know exactly".
  const envelopeOptions = useMemo<Array<string>>(
    () => [
      ...getVolumeBuckets(getSkusForRecurrence(skus, PlanRecurrence.MONTHLY))
        .filter((bucket) => bucket !== UNLIMITED_ENVELOPES)
        .map(String),
      'custom',
    ],
    [skus],
  )

  // "Outro" maps to unlimited demand: the engine then resolves to whatever
  // covers it (Starter Ilimitado when the features fit, Enterprise otherwise).
  // "Outro" carries the number the user typed; the ladder's own maths decides
  // whether an unlimited plan ends up cheaper than a smaller one plus add-ons.
  const desiredMonthlyVolume =
    envelopeOption === 'custom'
      ? Number(customEnvelopes) || 0
      : Number(envelopeOption ?? 0)

  const isEnvelopeStepValid =
    envelopeOption !== null &&
    (envelopeOption !== 'custom' || Number(customEnvelopes) > 0)

  const toggleFeature = (feature: FeatureKey) => {
    if (feature === 'eSignatures') return
    setSelectedFeatures((prev) =>
      prev.includes(feature)
        ? prev.filter((f) => f !== feature)
        : [...prev, feature],
    )
  }

  const handleNext = () => {
    // Track step completion
    if (currentStep === 1 && usageProfile) {
      trackPlanFinderUsageSelected({ usageProfile })
    } else if (currentStep === 2 && usageProfile) {
      trackPlanFinderEnvelopesSelected({
        usageProfile,
        envelopeOption: envelopeOption ?? '',
        desiredEnvelopes: desiredMonthlyVolume,
      })
    } else if (currentStep === 3 && usageProfile) {
      trackPlanFinderFeaturesSelected({
        usageProfile,
        selectedFeatures: [...selectedFeatures],
        desiredEnvelopes: desiredMonthlyVolume,
      })
    }
    setCurrentStep((prev) => prev + 1)
  }

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(0, prev - 1))
  }

  const resetState = () => {
    setTimeout(() => {
      setCurrentStep(0)
      setUsageProfile(null)
      setSelectedRecurrence('Yearly')
      setEnvelopeOption(null)
      setSelectedFeatures(['eSignatures'])
    }, 300)
  }

  const handleClose = () => {
    trackPlanFinderClosed({ step: currentStep, usageProfile })
    onOpenChange(false)
    resetState()
  }

  const handleSelectUsage = (profile: UsageProfile) => {
    setUsageProfile(profile)
    setEnvelopeOption(null)
  }

  const handleSelectPlan = (
    plan: RecommendedSku,
    recurrence: 'Monthly' | 'Yearly',
  ) => {
    trackPlanFinderPlanSelected({
      usageProfile: usageProfile!,
      recommendedPlan: plan.feature,
      selectedRecurrence: recurrence,
      desiredEnvelopes: desiredMonthlyVolume,
      selectedFeatures: [...selectedFeatures],
      monthlyPrice: plan.monthly?.price ?? 0,
      yearlyPrice: plan.yearly?.price ?? 0,
      currency: plan.currency,
    })

    // The recommendation usually needs add-on envelopes on top of the plan, and
    // the pre-checkout dialog opens on the bare allowance unless it is told
    // otherwise — so the demand travels with the deep link, in the units of the
    // recurrence being bought (a year carries twelve months of add-ons).
    // Named `totalEnvelopes`, not `envelopes`: that one is already a read-only
    // alias for the plan's volume bucket, emitted by the site.
    const quote = getRecurrenceQuote(plan, recurrence)

    // Same navigation the search dialog's quick actions use: when the user is
    // already on the plans page the router will not re-navigate for a search-only
    // change, so this pushes the params and lets nuqs pick them up.
    navigateWithSearchParams(
      `/settings/billing/plans?recurrence=${recurrence.toLowerCase()}` +
        `&volume=${plan.volume === UNLIMITED_ENVELOPES ? 'unlimited' : plan.volume}` +
        `&plan=${plan.feature.toLowerCase()}` +
        (quote.additionalEnvelopes > 0
          ? `&totalEnvelopes=${quote.envelopes}`
          : ''),
    )

    onOpenChange(false)
    resetState()
  }

  // Asking for "unlimited" is asking for infinite demand — coversVolume only
  // returns true for the unlimited SKU itself once the target is unreachable
  // by anything finite.
  const recommended = getRecommendedSku(
    selectedFeatures,
    desiredMonthlyVolume === UNLIMITED_ENVELOPES
      ? Number.MAX_SAFE_INTEGER
      : desiredMonthlyVolume,
    skus,
  )

  // Nothing at or above the feature floor can cover the demand (e.g.
  // unlimited volume + API & Webhooks — Business's ceiling is fixed and the
  // only unlimited variant is Starter). Enterprise is what the no-match state
  // points to instead of a priced recommendation; `whiteLabelResale`'s floor
  // is Max, and 0 demand is covered by any SKU, so this always resolves to
  // whichever Enterprise variant the catalog actually sells.
  const enterpriseFallback = recommended
    ? null
    : getRecommendedSku(['whiteLabelResale'], 0, skus)

  // Only ever rendered once `recommended` exists (see the currentStep === 4
  // branch below), so this fallback is display-safety, not a real fork.
  const currency = recommended?.currency ?? 'BRL'

  // Same derivation the grid uses, so the tip's percent never disagrees
  // between the two screens; null falls back to the entry variants, exactly
  // like the grid's neutral state.
  const savingsPercent = getAnnualSavingsPercent(
    skus,
    recommended?.volume ?? null,
  )

  // Quiz step index (1-based, excluding welcome)
  const quizStepIndex = currentStep - 1

  const progressBar = (
    <div className="flex items-center justify-between">
      <p className="text-muted-foreground text-xs font-medium">
        {t('stepIndicator', {
          current: quizStepIndex + 1,
          total: TOTAL_QUIZ_STEPS,
        })}
      </p>
      <div className="flex gap-1">
        {QUIZ_STEPS.map((_, idx) => (
          <div
            key={idx}
            className={cn(
              'h-1.5 w-6 rounded-full transition-colors',
              idx <= quizStepIndex ? 'bg-primary' : 'bg-muted',
            )}
          />
        ))}
      </div>
    </div>
  )

  let step: ReactNode
  if (currentStep === 0) {
    step = <WelcomeStep onStart={handleNext} />
  } else if (currentStep === 4) {
    // Handled explicitly either way — recommended or not — so this step
    // index can never fall through to the FeaturesStep catch-all below with
    // a runaway "Step 4 of 3" counter.
    step = recommended ? (
      <ResultStep
        recommended={recommended}
        currency={currency}
        selectedRecurrence={selectedRecurrence}
        onSelectRecurrence={setSelectedRecurrence}
        savingsPercent={savingsPercent}
        isSeasonal={isSeasonal}
        onBack={handleBack}
        onConfirm={() => handleSelectPlan(recommended, selectedRecurrence)}
      />
    ) : (
      <NoMatchStep
        onBack={handleBack}
        onViewEnterprise={
          enterpriseFallback
            ? () => handleSelectPlan(enterpriseFallback, selectedRecurrence)
            : undefined
        }
      />
    )
  } else if (currentStep === 1) {
    step = (
      <UsageStep
        progressBar={progressBar}
        usageProfile={usageProfile}
        onSelectUsage={handleSelectUsage}
        onBack={handleBack}
        onNext={handleNext}
      />
    )
  } else if (currentStep === 2) {
    step = (
      <EnvelopesStep
        progressBar={progressBar}
        envelopeOptions={envelopeOptions}
        envelopeOption={envelopeOption}
        customEnvelopes={customEnvelopes}
        onCustomEnvelopesChange={setCustomEnvelopes}
        onSelectOption={setEnvelopeOption}
        isValid={isEnvelopeStepValid}
        onBack={handleBack}
        onNext={handleNext}
      />
    )
  } else {
    step = (
      <FeaturesStep
        progressBar={progressBar}
        selectedFeatures={selectedFeatures}
        onToggleFeature={toggleFeature}
        onBack={handleBack}
        onNext={handleNext}
      />
    )
  }

  return (
    <ResponsiveDialog open={open} onOpenChange={handleClose}>
      <ResponsiveDialogContent>{step}</ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}
